/**
 * Ops platform color tokens — single source of truth for MUI theme, CSS, and
 * feature-specific palettes. Slate neutrals + warm orange brand; semantic hues
 * are tuned for comfortable contrast (not harsh default Material colours).
 */

export const APP_BRAND = {
  primary: {
    main: '#F4841A',
    light: '#F9A85C',
    dark: '#C66A00',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#2C3E50',
    light: '#3D5368',
    dark: '#1A2836',
    contrastText: '#FFFFFF',
  },
} as const;

export const APP_SEMANTIC = {
  success: {
    main: '#2F9E6F',
    light: '#56B88C',
    dark: '#247A56',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#D64545',
    light: '#E07070',
    dark: '#B33333',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#D4922A',
    light: '#E5AE58',
    dark: '#B5781F',
    contrastText: '#1A2332',
  },
  info: {
    main: '#4A7CAB',
    light: '#6E9BC4',
    dark: '#356189',
    contrastText: '#FFFFFF',
  },
} as const;

/** Accessible text — wired into MUI `palette.text`. */
export const APP_TEXT_COLOR = {
  light: {
    primary: '#1A2332',
    secondary: '#5C6778',
    disabled: '#8B95A5',
  },
  dark: {
    primary: '#EEF2F7',
    secondary: '#A8B3C0',
    disabled: '#7A8694',
  },
} as const;

export const APP_SURFACES = {
  light: {
    default: '#EEF1F5',
    paper: '#FFFFFF',
    elevated: '#F7F9FB',
    input: '#F4F6F9',
    inputFocused: '#FFFFFF',
    shell: '#FFFFFF',
  },
  dark: {
    default: '#11161D',
    paper: '#1A222C',
    elevated: '#222C38',
    input: '#28313D',
    inputFocused: '#2E3742',
    shell: '#1A222C',
  },
} as const;

export const APP_BORDERS = {
  light: {
    subtle: 'rgba(26, 35, 50, 0.06)',
    default: 'rgba(26, 35, 50, 0.10)',
    strong: 'rgba(26, 35, 50, 0.16)',
  },
  dark: {
    subtle: 'rgba(255, 255, 255, 0.06)',
    default: 'rgba(255, 255, 255, 0.09)',
    strong: 'rgba(255, 255, 255, 0.14)',
  },
} as const;

export const APP_ACTION = {
  light: {
    hover: 'rgba(26, 35, 50, 0.04)',
    selected: 'rgba(244, 132, 26, 0.10)',
    focusRing: 'rgba(244, 132, 26, 0.14)',
  },
  dark: {
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: 'rgba(244, 132, 26, 0.16)',
    focusRing: 'rgba(244, 132, 26, 0.22)',
  },
} as const;

/** Harmonized chart / map segment palette (brand-first). */
export const APP_CHART_COLORS = [
  APP_BRAND.primary.main,
  APP_SEMANTIC.info.main,
  APP_SEMANTIC.success.main,
  APP_SEMANTIC.warning.main,
  '#6B5B95',
  APP_SEMANTIC.error.main,
  '#3D8B8B',
  '#8B6F47',
] as const;

/** Corner radii from DESIGN.md section 4.4. */
export const APP_SHAPE = {
  radiusXs: 4,
  radiusSm: 6,
  radiusMd: 8,
  radiusLg: 10,
  radiusXl: 12,
} as const;

export type AppColorMode = 'light' | 'dark';

export function getAppSurfaces(mode: AppColorMode) {
  return APP_SURFACES[mode];
}

export function getAppBorders(mode: AppColorMode) {
  return APP_BORDERS[mode];
}

export function getAppAction(mode: AppColorMode) {
  return APP_ACTION[mode];
}

export function getAppCalloutPalette(mode: AppColorMode) {
  const isDark = mode === 'dark';
  return {
    info: {
      bg: isDark ? 'rgba(74, 124, 171, 0.14)' : '#EFF6FC',
      border: isDark ? 'rgba(110, 155, 196, 0.38)' : '#C5D9ED',
      color: isDark ? '#9CC0E0' : '#356189',
    },
    warning: {
      bg: isDark ? 'rgba(212, 146, 42, 0.14)' : '#FDF8EE',
      border: isDark ? 'rgba(229, 174, 88, 0.38)' : '#EDD9B0',
      color: isDark ? '#E5C078' : '#9A6418',
    },
    tip: {
      bg: isDark ? 'rgba(47, 158, 111, 0.12)' : '#EFF8F3',
      border: isDark ? 'rgba(86, 184, 140, 0.32)' : '#B8E0CC',
      color: isDark ? '#7FD4A8' : '#247A56',
    },
  } as const;
}

/** CSS custom properties — synced from theme via CssBaseline. */
export const APP_CSS_VARS = {
  primary: APP_BRAND.primary.main,
  primaryRgb: '244, 132, 26',
} as const;
